import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, addDoc, query, orderBy, limit, onSnapshot, serverTimestamp, deleteDoc, doc } from 'firebase/firestore';
import { Newspaper, Zap, Calendar, Loader2, Edit3, ExternalLink, Wrench, Lock, X, Hash, User, ArrowLeft, Share2, Mail, Send, CheckCircle2, Search, Inbox, Bookmark, Trash2, ChevronLeft, ChevronRight, AlertTriangle, Terminal, Menu } from 'lucide-react';

/* ===================================================================
   FIREBASE 설정
   ===================================================================
*/
const firebaseConfig = {
  apiKey: "AIzaSyAROfez5dxokDeT-XwjnFqYFvZaa3KuXAg",
  authDomain: "daily-news-ai.firebaseapp.com",
  projectId: "daily-news-ai",
  storageBucket: "daily-news-ai.firebasestorage.app",
  messagingSenderId: "290914561343",
  appId: "1:290914561343:web:d8ba3787decb8396bd778a",
  measurementId: "G-TPK27TMNS9"
};

// 앱 초기화 (중복 방지)
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

/* ===================================================================
   GEMINI API 키 (유료 플랜 적용)
   ===================================================================
*/
const GEMINI_API_KEY = "AIzaSyACFLy5IMgPGCgLRqzueNiC90cPgc5R1Wc";

// 관리자 계정 정보 [수정됨]
const ADMIN_CREDENTIALS = {
  id: "admin",
  pw: "gksksla1"
};

// 모델 옵션
const MODEL_OPTIONS = [
  { value: 'gemini-2.0-flash-exp', label: 'Gemini 2.0 Flash Exp (최신/추천)' },
  { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash (빠름)' },
  { value: 'gemini-1.5-flash-latest', label: 'Gemini 1.5 Flash Latest' },
  { value: 'gemini-pro', label: 'Gemini Pro (안정적)' },
];

// 뉴스 카테고리 정의 (8개)
const CATEGORIES = [
  "전체", "정치", "경제", "사회", "국제", "연예", "스포츠", "IT/과학", "문화"
];

// 생성용 카테고리 (전체 제외)
const TARGET_CATEGORIES = CATEGORIES.slice(1);

/* ===================================================================
   메인 컴포넌트
   ===================================================================
*/
export default function App() {
  const [user, setUser] = useState(null);
  const [articles, setArticles] = useState([]);
  const [inquiries, setInquiries] = useState([]); 
  const [isProcessing, setIsProcessing] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [selectedModel, setSelectedModel] = useState('gemini-2.0-flash-exp');
  
  // 관리자 및 UI 상태
  const [isAdmin, setIsAdmin] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [adminTab, setAdminTab] = useState('write'); 
  const [loginId, setLoginId] = useState('');
  const [loginPw, setLoginPw] = useState('');
  
  // 카테고리 필터 상태
  const [currentCategory, setCurrentCategory] = useState("전체");

  const [contactForm, setContactForm] = useState({ name: '', message: '' });

  // 화면 전환 상태 ('list' | 'detail')
  const [viewMode, setViewMode] = useState('list');
  const [currentArticle, setCurrentArticle] = useState(null);

  const scrollRef = useRef(null);

  // 1. 구글 폰트 로드
  useEffect(() => {
    const link = document.createElement('link');
    link.href = "https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@300;400;500;700&family=Noto+Serif+KR:wght@400;500;700;900&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  // 2. Hash Routing 처리
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#/article/')) {
        const articleId = hash.replace('#/article/', '');
        if (articles.length > 0) {
          const found = articles.find(a => a.id === articleId);
          if (found) {
            setCurrentArticle(found);
            setViewMode('detail');
            window.scrollTo(0, 0);
          }
        }
      } else {
        setViewMode('list');
        setCurrentArticle(null);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();
    
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [articles]);

  // 로그 자동 스크롤
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs]);

  // Firebase Init & Data Fetching
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (u) => {
      if (u) setUser(u);
      else signInAnonymously(auth).catch(err => console.error(err));
    });

    const qNews = query(collection(db, 'news_posts'), orderBy('createdAt', 'desc'), limit(50));
    const unsubscribeNews = onSnapshot(qNews, (snapshot) => {
      setArticles(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setInitLoading(false); 
    });

    const qInquiry = query(collection(db, 'inquiries'), orderBy('createdAt', 'desc'), limit(50));
    const unsubscribeInquiry = onSnapshot(qInquiry, (snapshot) => {
      setInquiries(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => {
      unsubscribeAuth();
      unsubscribeNews();
      unsubscribeInquiry();
    };
  }, []);

  const addLog = (message, type = 'info') => {
    setLogs(prev => [...prev, { message, type, time: Date.now() }]);
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginId === ADMIN_CREDENTIALS.id && loginPw === ADMIN_CREDENTIALS.pw) {
      setIsAdmin(true);
      setShowLoginModal(false);
      setLoginId('');
      setLoginPw('');
      addLog("🔓 관리자 모드 접속 성공", 'success');
    } else {
      alert("계정 정보가 올바르지 않습니다.");
    }
  };

  const handleLogout = () => {
    setIsAdmin(false);
    setViewMode('list');
    window.location.hash = '';
    addLog("🔒 관리자 로그아웃", 'info');
  };

  // -------------------------------------------------------------------
  // [기능] 뉴스 생성 (중복 방지 및 카테고리 로테이션)
  // -------------------------------------------------------------------
  const handleGenerateNews = async () => {
    if (isProcessing || !user) return;
    setIsProcessing(true);
    setLogs([]); 
    
    // [중복 방지] 최근 기사들의 제목 리스트 추출
    const recentTitles = articles.slice(0, 30).map(a => a.title).join(", ");
    
    // [카테고리 선정] 랜덤으로 하나의 카테고리 선택
    const targetCategory = TARGET_CATEGORIES[Math.floor(Math.random() * TARGET_CATEGORIES.length)];

    addLog(`📡 [작업 시작] 선택된 모델: ${selectedModel}`, 'info');
    addLog(`🎯 타겟 카테고리: [${targetCategory}]`, 'success');
    addLog(`🚫 중복 방지 필터 적용 중... (최근 ${articles.length}건 제외)`, 'info');

    try {
      const genUrl = `https://generativelanguage.googleapis.com/v1beta/models/${selectedModel}:generateContent?key=${GEMINI_API_KEY}`;
      
      // 1. 트렌드 분석 프롬프트
      const trendPrompt = `
        You are a Senior Data Analyst in South Korea.
        Analyze real-time search trends on Naver, Google Korea, and Daum.
        
        Task: Select ONE trending topic specifically related to the category: "${targetCategory}".
        
        CRITICAL RULE (NO REPEATS):
        Do NOT choose any of the following recent topics: [${recentTitles}]
        Find a FRESH topic that is currently hot.

        Output ONLY the topic name in Korean. No explanations.
      `;

      addLog(`🔍 [${targetCategory}] 분야 실시간 트렌드 분석 중...`, 'info');

      const trendResp = await fetch(genUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contents: [{ parts: [{ text: trendPrompt }] }] })
      });

      if (!trendResp.ok) {
        const errorData = await trendResp.json();
        throw new Error(`트렌드 분석 API 오류:\n${JSON.stringify(errorData, null, 2)}`);
      }

      const trendData = await trendResp.json();
      const trendTopic = trendData.candidates?.[0]?.content?.parts?.[0]?.text?.trim() || `${targetCategory} 관련 최신 이슈`;

      addLog(`📌 선정된 주제: '${trendTopic}'`, 'success');
      addLog(`⚖️ 팩트 체크 및 교차 검증 진행...`, 'info');

      await new Promise(r => setTimeout(r, 1200)); 
      addLog("✅ 검증 완료. 기사 작성 프로세스 진입.", 'success');

      // 2. 기사 작성 프롬프트 [수정됨]
      const articlePrompt = `
        Role: 전문가 수준의 시사 분석가이자 20년 경력의 수석 저널리스트.
        Topic: "${trendTopic}"
        Category: "${targetCategory}"
        
           당신은 "이슈 큐레이터형 블로그" 전문 작가입니다. 
    제공된 정보를 바탕으로 다음의 [8가지 전략]을 반드시 준수하여 티스토리 블로그 포스팅을 작성하세요.

    1. 제목 전략: 25~35자 내외, [의문형 + 감정 단어 + 이유] 공식 적용 (예: "~~ 사실이야?" ~~에 난리 난 이유)
    2. 도입부: 3줄 안에 독자의 스크롤을 멈추게 하는 강력한 후킹 문구.
    3. 본문 구조 (황금 구조):
       - 1구간: 왜 화제가 됐나 (언제, 어떤 사진/영상, 상황)
       - 2구간: 실제 네티즌 반응 (찬성 vs 반대, 커뮤니티 분위기 요약)
       - 3구간: 왜 이렇게 커졌나 (스타 이미지, 과거 맥락, 요즘 분위기 해석)
       - 4구간: 정리 멘트 (한 줄 요약 및 여운을 남기는 마무리)
    4. 문체: "다양한 반응을 보였다" 같은 AI 말투 금지. "분위기가 반으로 갈린다", "반응이 꽤 거세다" 같은 사람다운 구어체 사용.
    5. SEO: 본문에 키워드 3~5회 자연스럽게 포함, 소제목 활용.
    6. 길이: 1,000~1,500자 권장.
    7. 태그: 관련 키워드 8~12개 생성.
    8. 썸네일 문구: 6~10자 이내의 의문형/감정 단어 조합 추천.

        [이미지 프롬프트 지침]
        - 인물이나 얼굴은 절대 포함하지 마세요 (초상권 및 정책 준수).
        - 주제와 관련된 상징물, 현대적인 건축물, 추상적인 비즈니스 컨셉, 고화질 보도 사진 스타일로 묘사하세요.

        출력 형식은 반드시 아래 JSON 구조를 지키세요:
        { 
          "title": "여기에 독창적이고 클릭을 부르는 전문적인 제목 작성", 
          "summary": "기사의 핵심 내용을 1문장으로 요약", 
          "content": "서론\\n\\n(배경 분석)\\n\\n(심층 분석)\\n\\n(전망)\\n\\n[인사이트 뉴스의 시각]\\n마지막 결론 및 시사점", 
          "category": "${targetCategory}", 
          "keywords": ["핵심키워드1", "분석", "전망"],
          "imagePrompt": "English description for image, focus on objects/scenery, professional photography style"
        }
      `;

      const articleResp = await fetch(genUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contents: [{ parts: [{ text: articlePrompt }] }] })
      });

      if (!articleResp.ok) {
        const errorData = await articleResp.json();
        throw new Error(`기사 작성 API 오류:\n${JSON.stringify(errorData, null, 2)}`);
      }

      const articleData = await articleResp.json();
      
      if (!articleData.candidates?.length) {
         throw new Error("API 응답에 후보(candidate) 데이터가 없습니다.");
      }

      let rawText = articleData.candidates[0].content.parts[0].text;
      rawText = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
      
      let cleanJson;
      try {
        cleanJson = JSON.parse(rawText);
      } catch (e) {
        throw new Error(`JSON 파싱 실패. 원본 데이터:\n${rawText}`);
      }

      addLog(`✍️ 기사 초안 완료: ${cleanJson.title}`, 'info');
      addLog(`📸 보도용 이미지 생성 (인물 제외)...`, 'info');

      const finalImagePrompt = `editorial news photography, press photo, 8k, realistic, no human faces, no portraits, focus on objects or scenery, ${cleanJson.imagePrompt}`;
      const imagePromptEncoded = encodeURIComponent(finalImagePrompt);
      const generatedImageUrl = `https://image.pollinations.ai/prompt/${imagePromptEncoded}?width=1200&height=630&nologo=true&seed=${Math.floor(Math.random() * 99999)}`;

      await addDoc(collection(db, 'news_posts'), {
        title: cleanJson.title,
        summary: cleanJson.summary,
        content: cleanJson.content,
        category: cleanJson.category || targetCategory,
        keywords: cleanJson.keywords || [],
        createdAt: serverTimestamp(),
        imageUrl: generatedImageUrl, 
        imagePrompt: cleanJson.imagePrompt,
        verified: true
      });

      addLog("🎉 기사 발행 및 송고 완료.", 'success');

    } catch (error) {
      console.error(error);
      addLog(`❌ 치명적 오류 발생:\n${error.message}`, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmitContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.message) return alert("내용을 입력해주세요.");
    try {
      await addDoc(collection(db, 'inquiries'), {
        name: contactForm.name,
        message: contactForm.message,
        createdAt: serverTimestamp(),
        read: false
      });
      alert("소중한 의견 감사합니다. 접수되었습니다.");
      setContactForm({ name: '', message: '' });
      setShowContactModal(false);
    } catch (error) {
      alert("전송 실패: " + error.message);
    }
  };

  const handleOpenDetail = (article) => {
    window.location.hash = `#/article/${article.id}`;
  };

  const handleBackToList = () => {
    window.history.back(); 
  };

  const handleDeleteArticle = async (id, e) => {
    e.stopPropagation(); 
    if (window.confirm("이 기사를 DB에서 영구 삭제하시겠습니까?")) {
        try {
            await deleteDoc(doc(db, 'news_posts', id));
            addLog(`🗑️ 기사 삭제 완료 (ID: ${id})`, 'info');
        } catch (err) {
            alert("삭제 실패: " + err.message);
        }
    }
  };

  const handleDeleteInquiry = async (id) => {
    if (window.confirm("이 문의 내역을 삭제하시겠습니까?")) {
      await deleteDoc(doc(db, 'inquiries', id));
    }
  };

  // 초기 로딩 화면
  if (initLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white text-slate-800 font-sans">
        <Loader2 size={50} className="animate-spin text-slate-900 mb-6" />
        <h2 className="text-2xl font-serif font-bold tracking-widest mb-2">INSIGHT NEWS</h2>
        <p className="text-xs text-slate-500 uppercase tracking-widest">서버에 연결 중입니다...</p>
      </div>
    );
  }

  /* ===================================================================
     RENDER: 상세 페이지 (Detail View)
     ===================================================================
  */
  if (viewMode === 'detail' && currentArticle) {
    // 상세 페이지에서는 해당 기사의 카테고리와 같은 기사들을 추천하거나 앞뒤 기사를 보여줌
    // (여기서는 단순화를 위해 전체 리스트 기준 앞뒤 기사 유지)
    const currentIndex = articles.findIndex(a => a.id === currentArticle.id);
    const prevArticle = currentIndex > 0 ? articles[currentIndex - 1] : null; 
    const nextArticle = currentIndex < articles.length - 1 ? articles[currentIndex + 1] : null; 

    return (
      <div className="min-h-screen bg-white font-sans text-slate-900 animate-fade-in">
        {/* 상세 페이지 헤더 */}
        <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-slate-200 z-50 h-16 flex items-center justify-between px-4 max-w-5xl mx-auto w-full transition-all">
          <button 
            onClick={handleBackToList}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors group"
          >
            <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform"/> 
            <span className="font-medium">메인으로</span>
          </button>
          <div className="flex items-center gap-4">
            <button className="text-slate-400 hover:text-blue-600 transition-colors"><Share2 size={20} /></button>
            <button className="text-slate-400 hover:text-blue-600 transition-colors"><Bookmark size={20} /></button>
          </div>
        </header>

        <article className="pt-24 pb-20 px-4 max-w-3xl mx-auto">
          {/* 메타 정보 */}
          <div className="flex flex-wrap items-center gap-3 mb-6 border-b border-slate-100 pb-4">
            <span className="text-blue-800 font-bold text-xs tracking-widest uppercase bg-blue-50 px-2 py-1 rounded-sm">{currentArticle.category}</span>
            <span className="text-slate-300">|</span>
            <span className="text-slate-500 text-sm flex items-center gap-1 font-medium">
              {currentArticle.createdAt?.seconds ? new Date(currentArticle.createdAt.seconds * 1000).toLocaleString() : '방금 전'}
            </span>
            {currentArticle.verified && (
               <span className="ml-auto flex items-center gap-1 text-green-700 text-xs font-bold bg-green-50 px-2 py-1 rounded-full border border-green-100">
                 <CheckCircle2 size={12}/> 팩트 체크 완료
               </span>
            )}
          </div>

          {/* 제목 */}
          <h1 className="text-3xl md:text-5xl font-bold leading-tight text-slate-900 mb-8 font-serif break-keep tracking-tight">
            {currentArticle.title}
          </h1>

          {/* 메인 이미지 */}
          <figure className="w-full mb-10">
            <div className="relative overflow-hidden rounded-sm shadow-sm">
                <img 
                src={currentArticle.imageUrl} 
                alt={currentArticle.title} 
                className="w-full h-auto object-cover max-h-[500px]"
                />
            </div>
            <figcaption className="text-xs text-slate-400 mt-2 pl-1 flex items-center gap-1">
              <span className="font-bold">자료사진</span> AI 생성 (본문 내용 이해를 돕기 위한 이미지입니다)
            </figcaption>
          </figure>

          {/* 요약 박스 */}
          <div className="bg-slate-50 p-6 mb-10 border-l-4 border-slate-800 rounded-r-sm">
            <h4 className="font-bold text-slate-900 mb-2 text-xs uppercase tracking-widest">에디터 요약</h4>
            <p className="text-lg leading-relaxed text-slate-700 font-medium font-serif">
              {currentArticle.summary}
            </p>
          </div>

          {/* 본문 내용 */}
          <div className="prose prose-lg prose-slate max-w-none mb-16 font-serif">
            {currentArticle.content.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className="mb-6 text-slate-800 leading-8 text-[1.05rem] text-justify font-light">
                {paragraph}
              </p>
            ))}
          </div>

          {/* 태그 */}
          <div className="flex flex-wrap gap-2 mb-12">
            {currentArticle.keywords && currentArticle.keywords.map((tag, i) => (
              <span key={i} className="px-3 py-1 bg-white border border-slate-200 text-slate-600 text-sm rounded-full cursor-pointer hover:border-slate-400 transition-colors">
                #{tag}
              </span>
            ))}
          </div>

          {/* 이전/다음 글 네비게이션 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden mt-12 shadow-sm">
            {prevArticle ? (
                <div onClick={() => handleOpenDetail(prevArticle)} className="bg-white p-6 cursor-pointer hover:bg-slate-50 transition-colors flex flex-col items-start h-full">
                    <span className="text-xs text-slate-400 flex items-center gap-1 mb-2 font-bold uppercase"><ChevronLeft size={14}/> 다음 기사</span>
                    <h4 className="font-serif font-bold text-slate-800 line-clamp-2 leading-snug">{prevArticle.title}</h4>
                </div>
            ) : (
                <div className="bg-white p-6 text-slate-300 text-sm flex items-center justify-center">다음 기사가 없습니다.</div>
            )}
            
            {nextArticle ? (
                <div onClick={() => handleOpenDetail(nextArticle)} className="bg-white p-6 cursor-pointer hover:bg-slate-50 transition-colors flex flex-col items-end text-right h-full">
                    <span className="text-xs text-slate-400 flex items-center gap-1 mb-2 font-bold uppercase">이전 기사 <ChevronRight size={14}/></span>
                    <h4 className="font-serif font-bold text-slate-800 line-clamp-2 leading-snug">{nextArticle.title}</h4>
                </div>
            ) : (
                <div className="bg-white p-6 text-slate-300 text-sm flex items-center justify-center">이전 기사가 없습니다.</div>
            )}
          </div>
          
        </article>

        {/* --- Footer (법적 고지 및 사이트 정보) --- */}
        <footer className="bg-white border-t border-slate-200 pt-12 pb-20 mt-20">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <div className="flex justify-center gap-6 mb-6 text-xs font-bold text-slate-500 uppercase tracking-widest">
              <button onClick={() => alert("이용약관 내용: 본 사이트는 AI 기술을 활용한 뉴스 요약 정보를 제공하며, 정보의 정확성을 위해 노력하지만 최종 판단은 사용자에게 있습니다.")} className="hover:text-slate-900 transition-colors">이용약관</button>
              <button onClick={() => alert("개인정보처리방침: 본 사이트는 사용자의 개인정보를 수집하지 않으며, 구글 애드센스 등 광고 서비스를 위한 쿠키를 사용할 수 있습니다.")} className="hover:text-slate-900 transition-colors">개인정보처리방침</button>
              <button onClick={() => setShowContactModal(true)} className="hover:text-slate-900 transition-colors">고객센터</button>
            </div>
            
            <div className="mb-4">
              <h2 className="text-sm font-bold text-slate-400 font-serif italic tracking-tighter">
                INSIGHT <span className="text-slate-300">NEWS</span>
              </h2>
            </div>
            
            <p className="text-[10px] text-slate-400 leading-relaxed max-w-md mx-auto">
              (주) 인사이트뉴스 | 대표: 관리자 | 주소: 서울특별시 광진구 화양동 인근<br/>
              제휴 문의: {ADMIN_CREDENTIALS.id}@daily-news-ai.web.app<br/>
              © 2026 Insight News. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    );
  }

  // 필터링된 기사 목록
  const filteredArticles = currentCategory === "전체" 
    ? articles 
    : articles.filter(a => a.category === currentCategory);

  /* ===================================================================
     RENDER: 메인 페이지 (List View)
     ===================================================================
  */
  return (
    <div className="min-h-screen bg-[#FDFDFD] font-sans text-slate-900">
      
      {/* --- Header --- */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto px-4">
          <div className="h-16 flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer group" onClick={() => {window.location.hash=''; window.location.reload();}}>
              <div className="bg-slate-900 text-white p-1.5 rounded-sm shadow-md group-hover:rotate-3 transition-transform">
                <Newspaper className="h-5 w-5" />
              </div>
              <h1 className="text-xl font-bold tracking-tighter text-slate-900 font-serif">
                INSIGHT <span className="text-blue-800 font-light">NEWS</span>
              </h1>
            </div>
            
            <div className="flex items-center gap-4">
              <button onClick={() => setShowContactModal(true)} className="hidden md:flex items-center gap-1.5 text-xs font-bold uppercase text-slate-500 hover:text-slate-900 transition-colors tracking-wide border border-slate-200 px-3 py-1.5 rounded-sm hover:border-slate-400">
                 <Mail size={14}/> 제보 및 문의
              </button>
              {isAdmin ? (
                <button 
                  onClick={handleLogout}
                  className="text-xs font-bold text-slate-400 hover:text-red-500 uppercase flex items-center gap-1"
                >
                  로그아웃
                </button>
              ) : (
                <button 
                  onClick={() => setShowLoginModal(true)}
                  className="text-slate-400 hover:text-slate-900 transition-colors"
                >
                  <Lock size={16} />
                </button>
              )}
            </div>
          </div>
          
          {/* 카테고리 메뉴바 */}
          <nav className="flex overflow-x-auto gap-6 py-3 border-t border-slate-100 no-scrollbar">
            {CATEGORIES.map(category => (
              <button 
                key={category}
                onClick={() => setCurrentCategory(category)}
                className={`text-sm font-bold whitespace-nowrap transition-colors ${currentCategory === category ? 'text-blue-700' : 'text-slate-500 hover:text-slate-900'}`}
              >
                {category}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
        
        {/* --- 관리자 패널 --- */}
        {isAdmin && (
          <div className="lg:col-span-12 animate-fade-in-down mb-6">
            <div className="bg-slate-900 rounded-lg shadow-2xl overflow-hidden text-white border border-slate-800">
              <div className="px-6 py-4 border-b border-slate-700 flex justify-between items-center bg-black/40">
                <div className="flex items-center gap-6">
                  <h2 className="font-bold flex items-center gap-2 text-sm tracking-widest uppercase">
                    <Terminal size={16} className="text-blue-400"/> 관리자 콘솔
                  </h2>
                  <div className="flex bg-slate-800/50 rounded p-1 gap-1">
                    <button 
                      onClick={() => setAdminTab('write')}
                      className={`px-3 py-1 rounded-sm text-xs font-bold transition-all ${adminTab === 'write' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                    >
                      기사 발행
                    </button>
                    <button 
                      onClick={() => setAdminTab('inbox')}
                      className={`px-3 py-1 rounded-sm text-xs font-bold transition-all ${adminTab === 'inbox' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                    >
                      문의함 ({inquiries.filter(i => !i.read).length})
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                   <select 
                      value={selectedModel}
                      onChange={(e) => setSelectedModel(e.target.value)}
                      className="text-xs bg-slate-800 border border-slate-600 text-slate-300 rounded py-1 px-2 outline-none focus:border-blue-500"
                    >
                      {MODEL_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                </div>
              </div>
              
              {adminTab === 'write' ? (
                <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-1 space-y-4">
                     <div className="bg-gradient-to-br from-slate-800 to-slate-800/50 p-6 rounded-lg border border-slate-700">
                       <h3 className="font-bold text-white text-sm mb-2 flex items-center gap-2">
                         <Zap size={16} className="text-yellow-400"/> 뉴스 생성기
                       </h3>
                       <p className="text-xs text-slate-400 mb-6 leading-relaxed">
                         8대 카테고리 중 하나를 랜덤 선정하여<br/>
                         트렌드 분석 → 팩트 체크 → 작성 → 이미지 생성
                       </p>
                       <button 
                        onClick={handleGenerateNews} 
                        disabled={isProcessing}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-4 rounded font-bold text-sm transition-all shadow-lg
                          ${isProcessing 
                            ? 'bg-slate-700 text-slate-500 cursor-not-allowed' 
                            : 'bg-blue-600 hover:bg-blue-500 text-white hover:shadow-blue-500/20'}`}
                      >
                        {isProcessing ? <Loader2 className="animate-spin" size={16}/> : <Edit3 size={16} />}
                        {isProcessing ? "기사 작성 중..." : "새로운 기사 생성 (랜덤 카테고리)"}
                      </button>
                     </div>
                  </div>
                  
                  {/* 터미널 로그 */}
                  <div className="md:col-span-2 bg-black/80 rounded-lg p-4 h-56 overflow-y-auto font-mono text-[11px] leading-relaxed text-green-400 border border-slate-700 shadow-inner" ref={scrollRef}>
                      {logs.length === 0 ? <span className="opacity-50 text-slate-500">시스템 준비 완료... 명령 대기 중.</span> : logs.map((log, i) => (
                        <div key={i} className={`mb-1.5 break-all whitespace-pre-wrap border-l-2 pl-2 ${log.type === 'error' ? 'text-red-400 border-red-500 bg-red-900/10' : 'border-slate-700'}`}>
                          <span className="text-slate-500 mr-2 select-none">[{new Date(log.time).toLocaleTimeString()}]</span>
                          {log.message}
                        </div>
                      ))}
                  </div>
                </div>
              ) : (
                <div className="p-6 h-64 overflow-y-auto">
                  <table className="w-full text-sm text-left text-slate-300">
                    <thead className="text-xs text-slate-500 uppercase bg-slate-900 border-b border-slate-700">
                      <tr>
                        <th className="px-4 py-3">보낸이</th>
                        <th className="px-4 py-3">내용</th>
                        <th className="px-4 py-3">날짜</th>
                        <th className="px-4 py-3 text-right">관리</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inquiries.length === 0 ? (
                        <tr><td colSpan="4" className="px-4 py-8 text-center text-slate-600 italic">수신된 문의가 없습니다.</td></tr>
                      ) : (
                        inquiries.map((inq) => (
                          <tr key={inq.id} className="border-b border-slate-700 hover:bg-slate-800 transition-colors">
                            <td className="px-4 py-3 font-bold text-white">{inq.name}</td>
                            <td className="px-4 py-3 max-w-xs truncate">{inq.message}</td>
                            <td className="px-4 py-3 text-xs text-slate-500">{inq.createdAt?.seconds ? new Date(inq.createdAt.seconds * 1000).toLocaleDateString() : '-'}</td>
                            <td className="px-4 py-3 text-right">
                              <button onClick={() => handleDeleteInquiry(inq.id)} className="text-slate-500 hover:text-red-400 transition-colors p-1"><Trash2 size={14}/></button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* --- 메인 뉴스 리스트 --- */}
        <div className="lg:col-span-8">
          <div className="flex items-center justify-between mb-8 pb-3 border-b-2 border-slate-900">
             <h2 className="text-2xl font-bold text-slate-900 font-serif tracking-tight">
               {currentCategory === "전체" ? "최신 주요 뉴스" : `${currentCategory} 뉴스`}
             </h2>
             <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
               {new Date().toLocaleDateString()}
             </span>
          </div>

          <div className="space-y-12">
            {filteredArticles.length === 0 && (
              <div className="py-32 text-center border-2 border-dashed border-slate-200 rounded-lg bg-slate-50">
                <p className="text-slate-400 font-medium">'{currentCategory}' 카테고리에 해당하는 기사가 없습니다.</p>
                <p className="text-slate-300 text-sm mt-2">다른 메뉴를 선택하거나 기사를 생성해주세요.</p>
              </div>
            )}
            
            {filteredArticles.map(article => (
              <div key={article.id} className="group cursor-pointer flex flex-col md:flex-row gap-8 items-start relative" onClick={() => handleOpenDetail(article)}>
                {/* 관리자 삭제 버튼 */}
                {isAdmin && (
                    <button 
                        onClick={(e) => handleDeleteArticle(article.id, e)}
                        className="absolute -top-3 -right-3 md:left-0 md:-top-2 md:right-auto bg-white border border-slate-200 text-slate-400 p-2 rounded-full hover:bg-red-50 hover:text-red-500 hover:border-red-200 shadow-sm z-20 transition-all opacity-0 group-hover:opacity-100"
                        title="기사 삭제"
                    >
                        <Trash2 size={14} />
                    </button>
                )}

                <div className="md:w-5/12 w-full aspect-[4/3] md:aspect-[4/3] overflow-hidden rounded-sm relative bg-slate-100 shadow-sm">
                  <img 
                    src={article.imageUrl} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 filter saturate-[0.9] group-hover:saturate-100" 
                    alt="thumbnail" 
                    onError={(e) => {e.target.src = 'https://via.placeholder.com/600x400?text=No+Image'}}
                  />
                  {article.verified && (
                    <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-md text-white text-[10px] font-bold px-2 py-1 rounded-sm flex items-center gap-1 border border-white/20">
                      <CheckCircle2 size={10}/> 팩트 체크됨
                    </div>
                  )}
                </div>
                
                <div className="md:w-7/12 w-full flex flex-col h-full py-1">
                  <div className="mb-3 flex items-center gap-2">
                    <span className="text-blue-700 font-bold text-[10px] uppercase tracking-widest border border-blue-100 px-1.5 py-0.5 rounded-sm bg-blue-50/50">{article.category}</span>
                    <span className="text-slate-300 text-[10px]">•</span>
                    <span className="text-slate-400 text-[11px] font-medium">{article.createdAt?.seconds ? new Date(article.createdAt.seconds * 1000).toLocaleDateString() : 'Just now'}</span>
                  </div>
                  
                  <h3 className="font-bold text-xl md:text-2xl mb-3 leading-tight text-slate-900 group-hover:text-blue-800 transition-colors font-serif break-keep">
                    {article.title}
                  </h3>
                  
                  <p className="text-slate-500 text-sm md:text-[15px] line-clamp-3 mb-5 leading-relaxed font-light">
                    {article.summary}
                  </p>
                  
                  <div className="mt-auto pt-4 border-t border-slate-100 w-full">
                     <span className="text-xs font-bold text-slate-900 uppercase tracking-wide group-hover:underline decoration-blue-500 underline-offset-4 decoration-2">기사 읽기</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* --- 사이드바 --- */}
        <div className="lg:col-span-4 space-y-10">
          
          {/* 트렌드 순위 */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
             <div className="flex items-center justify-between mb-5">
               <h3 className="font-bold text-slate-900 text-xs uppercase tracking-widest flex items-center gap-2">
                 <Zap size={14} className="text-red-500"/> 실시간 트렌드
               </h3>
               <span className="text-[10px] text-slate-400">실시간</span>
             </div>
             <ul className="space-y-4">
               {['반도체 수출 실적', '서울 부동산 전망', '신규 드라마 시청률', 'AI 스타트업 투자', '국내 프로야구'].map((item, idx) => (
                 <li key={idx} className="flex items-center gap-3 text-sm text-slate-700 hover:text-blue-700 cursor-pointer transition-colors group">
                   <span className="font-bold text-slate-300 w-4 group-hover:text-blue-500 italic font-serif">{idx + 1}</span> 
                   <span className="group-hover:translate-x-1 transition-transform">{item}</span>
                 </li>
               ))}
             </ul>
          </div>

          {/* 문의하기 배너 */}
          <div className="bg-slate-100 rounded-sm p-8 text-center border border-slate-200">
            <h3 className="font-bold text-lg mb-2 font-serif text-slate-800">제보하기</h3>
            <p className="text-slate-500 text-xs mb-6 leading-relaxed">
              독자 여러분의 소중한 제보를 기다립니다.<br/>
              팩트 체크 후 기사화됩니다.
            </p>
            <button onClick={() => setShowContactModal(true)} className="w-full py-3 bg-white border border-slate-300 hover:border-slate-800 text-slate-800 font-bold rounded-sm transition-all text-xs uppercase tracking-wide shadow-sm hover:shadow-md">
              제보 보내기
            </button>
          </div>
          
          <div className="text-[10px] text-slate-300 text-center leading-relaxed">
            © 2024 Insight News. <br/>
            All rights reserved. Powered by AI Technology.
          </div>
        </div>
      </main>

      {/* --- Footer (법적 고지 및 사이트 정보) --- */}
      <footer className="bg-white border-t border-slate-200 pt-12 pb-20 mt-20">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="flex justify-center gap-6 mb-6 text-xs font-bold text-slate-500 uppercase tracking-widest">
            <button onClick={() => alert("이용약관 내용: 본 사이트는 기술을 활용한 뉴스 요약 정보를 제공하며, 정보의 정확성을 위해 노력하지만 최종 판단은 사용자에게 있습니다.")} className="hover:text-slate-900 transition-colors">이용약관</button>
            <button onClick={() => alert("개인정보처리방침: 본 사이트는 사용자의 개인정보를 수집하지 않으며, 구글 애드센스 등 광고 서비스를 위한 쿠키를 사용할 수 있습니다.")} className="hover:text-slate-900 transition-colors">개인정보처리방침</button>
            <button onClick={() => setShowContactModal(true)} className="hover:text-slate-900 transition-colors">고객센터</button>
          </div>
          
          <div className="mb-4">
            <h2 className="text-sm font-bold text-slate-400 font-serif italic tracking-tighter">
              INSIGHT <span className="text-slate-300">NEWS</span>
            </h2>
          </div>
          
          <p className="text-[10px] text-slate-400 leading-relaxed max-w-md mx-auto">
            (주) 인사이트뉴스 | 대표: 하 진 | 주소: 서울특별시 광진구 화양동 <br/>
            제휴 문의: {ADMIN_CREDENTIALS.id}@daily-news-ai.web.app<br/>
            © 2026 Insight News. All rights reserved.
          </p>
        </div>
      </footer>

      {/* --- [모달] 관리자 로그인 --- */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded shadow-2xl max-w-sm w-full p-8 border border-slate-200">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest">관리자 접속</h3>
              <button onClick={() => setShowLoginModal(false)} className="text-slate-400 hover:text-slate-600"><X size={20}/></button>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <input type="text" value={loginId} onChange={(e) => setLoginId(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm outline-none focus:border-blue-500 transition-colors" placeholder="아이디" />
              <input type="password" value={loginPw} onChange={(e) => setLoginPw(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm outline-none focus:border-blue-500 transition-colors" placeholder="비밀번호" />
              <button type="submit" className="w-full bg-slate-900 text-white font-bold py-3.5 rounded-sm text-xs hover:bg-slate-800 transition-colors uppercase tracking-widest">로그인</button>
            </form>
          </div>
        </div>
      )}

      {/* --- [모달] 문의하기 --- */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded shadow-2xl max-w-md w-full p-8 border border-slate-200">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-slate-900 font-serif">뉴스룸 문의</h3>
              <button onClick={() => setShowContactModal(false)} className="text-slate-400 hover:text-slate-600"><X size={20}/></button>
            </div>
            <form onSubmit={handleSubmitContact} className="space-y-4">
              <div>
                 <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase tracking-wide">이름</label>
                 <input 
                   type="text" 
                   value={contactForm.name} 
                   onChange={(e) => setContactForm({...contactForm, name: e.target.value})} 
                   className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-2 text-sm outline-none focus:border-blue-500" 
                   placeholder="이름을 입력하세요"
                 />
              </div>
              <div>
                 <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase tracking-wide">내용</label>
                 <textarea 
                   value={contactForm.message} 
                   onChange={(e) => setContactForm({...contactForm, message: e.target.value})} 
                   className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-2 text-sm outline-none focus:border-blue-500 min-h-[120px]" 
                   placeholder="문의 내용을 입력하세요..."
                 />
              </div>
              <button type="submit" className="w-full bg-blue-600 text-white font-bold py-3 rounded-sm text-xs hover:bg-blue-700 flex items-center justify-center gap-2 uppercase tracking-wide">
                <Send size={14}/> 문의 접수
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}